/**
 * Component to display nearby spots from user's Mekke and favorites
 */
import { useTranslation } from "react-i18next";
import { MapPin, Compass, Heart, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { cn } from "@tabipla/ui/lib/utils";
import { formatDistance } from "@tabipla/shared";
import { useNearbySpots, hasNearbySpots } from "../../application/hooks/useNearbySpots";
import type { NearbyMekkeSpotDto, NearbyFavoriteSpotDto } from "@tabipla/shared";

interface NearbySuggestionsProps {
  latitude?: number | null;
  longitude?: number | null;
  onSelectMekkeSpot?: (spot: NearbyMekkeSpotDto) => void;
  onSelectFavoriteSpot?: (spot: NearbyFavoriteSpotDto) => void;
  radiusKm?: number;
  className?: string;
}

export function NearbySuggestions({
  latitude,
  longitude,
  onSelectMekkeSpot,
  onSelectFavoriteSpot,
  radiusKm = 10,
  className,
}: NearbySuggestionsProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const { data, isLoading } = useNearbySpots({
    latitude,
    longitude,
    radiusKm,
    limit: 5,
    enabled: latitude != null && longitude != null,
  });

  // Don't render if no coordinates or no nearby spots
  if (latitude == null || longitude == null) return null;
  if (!isLoading && !hasNearbySpots(data)) return null;

  return (
    <div className={cn("rounded-lg border bg-muted/30 p-3", className)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="h-4 w-4 text-primary" />
          {t("spots.nearbySuggestions", { defaultValue: "周辺のおすすめスポット" })}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Mekke spots */}
              {data?.mekkeSpots && data.mekkeSpots.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Compass className="h-3.5 w-3.5" />
                    Mekke
                  </div>
                  {data.mekkeSpots.map((spot) => (
                    <NearbySpotCard
                      key={spot.id}
                      name={spot.name}
                      category={spot.category}
                      distanceKm={spot.distanceKm}
                      imageUrl={spot.images[0]?.url}
                      statusBadge={
                        spot.status === "want_to_go" ? (
                          <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                            {t("mekke.wantToGo", { defaultValue: "行きたい" })}
                          </span>
                        ) : (
                          <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                            {t("mekke.visited", { defaultValue: "訪問済み" })}
                          </span>
                        )
                      }
                      onClick={() => onSelectMekkeSpot?.(spot)}
                    />
                  ))}
                </div>
              )}

              {/* Favorite spots */}
              {data?.favoriteSpots && data.favoriteSpots.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Heart className="h-3.5 w-3.5" />
                    {t("spots.favorites", { defaultValue: "お気に入り" })}
                  </div>
                  {data.favoriteSpots.map((spot) => (
                    <NearbySpotCard
                      key={spot.id}
                      name={spot.name}
                      category={spot.category}
                      distanceKm={spot.distanceKm}
                      onClick={() => onSelectFavoriteSpot?.(spot)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface NearbySpotCardProps {
  name: string;
  category: string;
  distanceKm: number;
  imageUrl?: string;
  statusBadge?: React.ReactNode;
  onClick?: () => void;
}

function NearbySpotCard({
  name,
  category,
  distanceKm,
  imageUrl,
  statusBadge,
  onClick,
}: NearbySpotCardProps) {
  const { t } = useTranslation();

  const categoryLabels: Record<string, string> = {
    sightseeing: t("spots.category.sightseeing", { defaultValue: "観光" }),
    food: t("spots.category.food", { defaultValue: "グルメ" }),
    activity: t("spots.category.activity", { defaultValue: "アクティビティ" }),
    transport: t("spots.category.transport", { defaultValue: "移動" }),
    accommodation: t("spots.category.accommodation", { defaultValue: "宿泊" }),
    restaurant: t("spots.category.food", { defaultValue: "グルメ" }),
    cafe: t("spots.category.cafe", { defaultValue: "カフェ" }),
    landmark: t("spots.category.sightseeing", { defaultValue: "観光" }),
    park: t("spots.category.park", { defaultValue: "公園" }),
    museum: t("spots.category.museum", { defaultValue: "美術館" }),
    shop: t("spots.category.shop", { defaultValue: "ショップ" }),
    other: t("spots.category.other", { defaultValue: "その他" }),
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md bg-background p-2 text-left transition-colors hover:bg-accent"
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="h-10 w-10 shrink-0 rounded-md object-cover" />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{categoryLabels[category] || category}</span>
          <span>•</span>
          <span>{formatDistance(distanceKm)}</span>
          {statusBadge}
        </div>
      </div>
    </button>
  );
}
